"use client";

import ApprovalContentParts from "./ApprovalContentParts";

import clsx from "clsx";
import {useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {DEFAULT_BRAND} from "@/enums/brands";
import {THEME} from "@/enums/profile";
import {Chart, MessageContentBuildWorkflow, MessageContentFunctionCall, MessageContentPieChart, MessageContentScriptDraft} from "@/services/wingman";
import {useStableCallback} from "@/hooks/useStableCallback";
import {FeedbackCategory, FeedbackRating, MessageFeedbackData} from "@/types/feedback";
import {TokenBreakdown} from "@/utils/chat/tokenEstimation";
import type {FileUploadConfig} from "@/utils/fileHelpers";
import {computeTurnSlotMeta, findLastAssistantIndex, hasInProgressToolCalls, hasAssistantContentAfterLastUser} from "@/utils/chat/chatViewUtils";
import {
   parseTicketConfirmationChoices,
   ticketConfirmationChoicesFromParts,
} from "@/utils/chat/ticketConfirmationChoices";
import AskBarChart from "../ask/AskBarChart";
import AskPieChart from "../ask/AskPieChart";
import {CitationSource} from "@/types/ask";
import AskScriptDraftBubble from "../ask/AskScriptDraftBubble";
import AskWorkflowBuilderBubble from "../ask/AskWorkflowBuilderBubble";
import AskText from "../ask/AskText";
import AskToolCall from "../ask/AskToolCall";
import {
   type AssistantContentPart,
} from "../ask/AssistantContentParts";
import TicketConfirmSuggestions from "../ask/TicketConfirmSuggestions";
import MessageBubble from "../ask/MessageBubble";
import MessageFeedback from "../ask/MessageFeedback";
import RecalledMemoriesFooter from "./RecalledMemoriesFooter";
import SourceFooter from "./SourceFooter";
import GailThinkingWords from "./GailThinkingWords";
import {useDocumentPreview, DocumentPreviewProvider} from "@/hooks/useDocumentPreview";
import dynamic from "next/dynamic";
const DocumentPreviewSheet = dynamic(() => import("@/components/knowledge/DocumentPreviewSheet"), { ssr: false });
import FileDropZone from "../common/FileDropZone";
import {Assistant} from "./AssistantSelector";
import ChatInput from "./ChatInput";
import GailGptReadAloud from "./GailGptReadAloud";
import type {ToolApprovalResponse} from "@/services/wingman";
import type {AgentSkill} from "@/types/skills";
import {useChatSkills} from "./useChatSkills";

export interface ChatMessage {
   approvalMessageIds?: Record<string, string>;
   text: string;
   isUser: boolean;
   role?: string;
   isAnimated?: boolean;

   fileData?:
      | {
           type: "binary";
           mimeType: string;
           data: string; // base64 encoded
           /** Remote file location when `data` isn't inlined base64. */
           url?: string;
        }
      | {
           type: "generated";
           mimeType: string;
           fileName: string;
           downloadUrl: string;
           objectPath: string;
        };
   chartData?: {
      type: "pie" | "bar" | "loading";
      data: MessageContentPieChart["chart"] | Chart;
   };
   functionCallData?: MessageContentFunctionCall;
   // Script draft workspace block (rendered as a card that opens the workspace panel)
   scriptDraftData?: MessageContentScriptDraft;
   // Workflow-builder handoff (rendered as a card; navigates to the builder on arrival)
   buildWorkflowData?: MessageContentBuildWorkflow;
   // Whether the function call has completed (has a matching function_result)
   functionCallCompleted?: boolean;
   // The result of the function call (for displaying sources, etc.)
   functionCallResult?: unknown;
   // Ordered content parts for multi-part messages (preserves order of text and function calls)
   contentParts?: AssistantContentPart[];
   // Citations produced by web search / document query
   citations?: CitationSource[];
   // Message feedback
   messageId?: string;
   feedback?: MessageFeedbackData;
   // Follow-up suggestions (v2)
   followUpSuggestions?: string[];
   /** Live-only Mem0 facts recalled for this turn. */
   recalledFacts?: string[];
}

interface ChatViewProps {
   skills?: AgentSkill[] | null;
   messages: ChatMessage[];
   conversationId?: string;
   inputValue: string;
   onInputChange: (value: string) => void;
   /**
    * Sends a message. May resolve `false` when the chat refused it (a stream is
    * still active); programmatic senders such as the after-call workflow card
    * keep their message queued in that case and retry once the chat is idle.
    */
   onSendMessage: (messageText?: string) => void | boolean | Promise<void | boolean>;
   onToolApprovalResponse?: (decision: ToolApprovalResponse) => Promise<void>;
   /** Latest underlying assistant message eligible for approval, before display splitting. */
   activeToolApprovalMessageId?: string;
   isSubmitting?: boolean;
   /** Cancels the in-flight stream; shows a stop button in the composer while working. */
   onCancelStream?: () => void;
   disabled?: boolean;
   className?: string;
   isBannerActive?: boolean;
   senderName?: string;
   senderIcon?: string;
   assistants?: Assistant[];
   selectedAssistant?: Assistant;
   onAssistantChange?: (assistant: Assistant) => void;
   inputPlaceholder?: string;
   brand?: string;
   theme?: THEME;
   // File upload props
   onFilesSelected?: (files: File[]) => Promise<void> | void;
   attachedFiles?: File[];
   onFilesChange?: (files: File[]) => void;
   fileUploadConfig?: FileUploadConfig;
   onInvalidFiles?: (rejectedFiles: {file: File; reason: string}[]) => void;
   // Loading states
   isLoadingAssistants?: boolean;
   fileCompatibilityTooltip?: string;
   isStreaming?: boolean;
   streamingStatus?: string | null;
   onFeedback?: (
      messageId: string,
      rating: FeedbackRating | null,
      categories?: FeedbackCategory[],
      comment?: string
   ) => void;
   canEditModel?: boolean;
   onModelChange?: (assistantId: string, newModel: string) => void;
   isModelSaving?: boolean;
   tokenLimitReached?: boolean;
   tokenUsagePercent?: number;
   tokenBreakdown?: TokenBreakdown;
   /**
    * Fill the parent element instead of assuming viewport-height layout.
    * Used by embedded surfaces (the assistant dock) where the chat doesn't
    * own the page.
    */
   fillContainer?: boolean;
}

const renderChartContent = (
   chartMessage: ChatMessage,
   t: (key: string) => string,
   theme: THEME,
   brand: string,
) => {
   const chartType = chartMessage.chartData!.type;

   if (chartType === "loading") {
      return (
         <output className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
            <div className="flex flex-col items-center space-y-2">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
               <span className="sr-only">{t("generatingChart")}</span>
            </div>
         </output>
      );
   }

   if (chartType === "pie") {
      return (
         <AskPieChart
            chartData={chartMessage.chartData!.data as MessageContentPieChart["chart"]}
            themeOverride={theme}
            brandOverride={brand}
         />
      );
   }

   return (
      <AskBarChart
         chartData={chartMessage.chartData!.data as Chart}
         themeOverride={theme}
         brandOverride={brand}
      />
   );
};

const messageHasReplyText = (message: ChatMessage | undefined): boolean => {
   if (!message || message.isUser) return false;
   if (
      message.contentParts?.some(
         (part) => part.type === "text" && part.content.trim().length > 0
      )
   ) {
      return true;
   }
   return typeof message.text === "string" && message.text.trim().length > 0;
};

// Check if chart message can be inlined with the next message
const canInlineChartWithNext = (nextMessage: ChatMessage | undefined): boolean => {
   if (!nextMessage) return false;
   if (nextMessage.isUser) return false;
   if (nextMessage.fileData || nextMessage.chartData || nextMessage.functionCallData || nextMessage.scriptDraftData) return false;
   return typeof nextMessage.text === "string" && nextMessage.text.length > 0;
};

// Breathing room kept above a freshly sent user turn when it pins to the top
// of the scroll area.
const USER_TURN_TOP_GAP = 24;

const ChatView: React.FC<ChatViewProps> = ({
   skills,
   messages,
   conversationId,
   inputValue,
   onInputChange,
   onSendMessage,
   onToolApprovalResponse,
   activeToolApprovalMessageId,
   isSubmitting = false,
   onCancelStream,
   disabled = false,
   className,
   isBannerActive = false,
   senderName = "Gail",
   assistants,
   selectedAssistant,
   onAssistantChange,
   inputPlaceholder = "",
   brand = DEFAULT_BRAND,
   theme = THEME.LIGHT,
   onFilesSelected,
   attachedFiles = [],
   onFilesChange,
   fileUploadConfig,
   onInvalidFiles,
   isLoadingAssistants = false,
   fileCompatibilityTooltip,
   isStreaming = false,
   streamingStatus = null,
   onFeedback,
   canEditModel,
   onModelChange,
   isModelSaving,
   tokenLimitReached = false,
   tokenUsagePercent = 0,
   tokenBreakdown,
   fillContainer = false,
}) => {
   const {t} = useTranslation("ask");
   const displaySkills = useChatSkills(skills);
   const chatContainerRef = useRef<HTMLDivElement>(null);
   const {openDocument, previewProps} = useDocumentPreview();

   // Anchor that pins a new turn toward the top: cqh tracks the embedded dock scroll area; dvh tracks the full-page viewport.
   const anchorTurnClass = fillContainer ? "min-h-[80cqh]" : "min-h-[50dvh] md:min-h-[70dvh]";

   // Stable-identity wrappers so that `renderedMessageList` (a heavy useMemo)
   // doesn't re-compute on every keystroke due to new callback/t references.
   const hasFeedbackHandler = useRef(!!onFeedback);
   hasFeedbackHandler.current = !!onFeedback;
   const stableFeedbackHandler = useStableCallback(onFeedback ?? (() => {}));
   const stableT = useStableCallback(t);

   const scrollToBottom = () => {
      if (chatContainerRef.current) {
         chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
   };

   // AI is working if submitting initial request, loading assistants, or actively streaming
   const isAiWorking = isSubmitting || isLoadingAssistants || isStreaming;

   const lastUserIndex = useMemo(() => messages.findLastIndex((m) => m.isUser), [messages]);
   const endsWithUserMessage = lastUserIndex >= 0 && lastUserIndex === messages.length - 1;

   const prevLastUserIndexRef = useRef(-1);

   useEffect(() => {
      const timeoutId = setTimeout(() => {
         const container = chatContainerRef.current;
         if (!container) return;

         const isNewTurn = lastUserIndex !== prevLastUserIndexRef.current;
         prevLastUserIndexRef.current = lastUserIndex;

         // A fresh turn pins its first user bubble to the top; the anchored
         // min-height on the trailing assistant block reserves the scroll room.
         // Scrolling to the very bottom would over-scroll tall user turns (e.g.
         // file attachment cards) and crop the top of the first bubble.
         const pinTarget = container.querySelector<HTMLElement>('[data-last-user-turn="true"]');
         if (isNewTurn && pinTarget && (isAiWorking || endsWithUserMessage)) {
            container.scrollTop = Math.max(0, pinTarget.offsetTop - USER_TURN_TOP_GAP);
            return;
         }
         // Loading an existing conversation keeps the latest content in view;
         // mid-turn message changes (streaming parts, tool cards) keep the
         // current scroll position instead of yanking it.
         if (isNewTurn) scrollToBottom();
      }, 50);

      return () => clearTimeout(timeoutId);
   }, [messages.length, isSubmitting, lastUserIndex, endsWithUserMessage, isAiWorking]);

   // Memoize message list rendering so keystrokes in the input don't re-render all messages
   const renderedMessageList = useMemo(() => {
      const lastAssistantMessageIndex = findLastAssistantIndex(messages, isAiWorking);
      const isThinkingVisible = isAiWorking && !hasInProgressToolCalls(messages);
      const hasAnyAssistantContent = hasAssistantContentAfterLastUser(messages);
      const turnMeta = computeTurnSlotMeta(messages);

      const trailingAssistantMessage =
         lastAssistantMessageIndex >= 0 ? messages[lastAssistantMessageIndex] : undefined;
      const isStreamingReplyText = messageHasReplyText(trailingAssistantMessage);

      const showInlineThinking =
         isThinkingVisible && hasAnyAssistantContent && !isStreamingReplyText;
      const showInitialThinking = isThinkingVisible && !hasAnyAssistantContent;

      const isLastAssistant = (index: number) => index === lastAssistantMessageIndex;

      const getCommonProps = (message: ChatMessage, index: number) => {
         const nextMessage = messages[index + 1];
         return {
            isUser: message.isUser,
            agentName: message.isUser ? undefined : senderName,
            themeOverride: theme,
            brandOverride: brand,
            className: clsx(
               message.isUser ? "ml-auto" : "",
               // Assistant messages are full-width within the 768px container
               message.isUser ? "max-w-[75%]" : ""
            ),
            isLastInGroup: message.isUser ? !nextMessage?.isUser : true,
         };
      };

      // Count files from the latest user turn only (after the last assistant message)
      const lastAssistantMsgIdx = messages.findLastIndex((m) => !m.isUser);
      const latestUserMessages = messages.slice(lastAssistantMsgIdx + 1);
      const fileCount = latestUserMessages.filter((m) => m.isUser && m.fileData).length;

      const renderStatusText = () => {
         if (!streamingStatus) return null;
         const key = streamingStatus === "analyzing_document" && fileCount > 1
            ? "status.analyzing_documents"
            : `status.${streamingStatus}`;
         return t(key, {count: fileCount, defaultValue: t("status.default")});
      };

      const renderThinkingContent = () => {
         const specificStatus =
            streamingStatus && streamingStatus !== "default" ? renderStatusText() : null;
         return specificStatus ? (
            <span className="text-sm text-500 animate-text-shimmer">{specificStatus}</span>
         ) : (
            <GailThinkingWords />
         );
      };

      const renderThinkingIndicator = (index: number) =>
         isLastAssistant(index) && showInlineThinking ? (
            <div className="mt-3">{renderThinkingContent()}</div>
         ) : null;

      const getMessageReadAloudText = (message: ChatMessage): string => {
         if (message.contentParts?.length) {
            return message.contentParts
               .filter((part): part is AssistantContentPart & {type: "text"; content: string} => part.type === "text")
               .map((part) => part.content)
               .join("\n\n");
         }

         return message.text;
      };

      const renderFunctionCallMessage = (message: ChatMessage, index: number, commonProps: ReturnType<typeof getCommonProps>) => {
         const isToolCompleted = message.functionCallCompleted ?? false;
         return (
            <MessageBubble
               type="regular"
               {...commonProps}
               className={clsx(
                  commonProps.className,
                  !isToolCompleted && anchorTurnClass
               )}
            >
               <AskToolCall
                  data={message.functionCallData!}
                  isCompleted={isToolCompleted}
                  result={message.functionCallResult}
                  steps={[{
                     data: message.functionCallData!,
                     isCompleted: isToolCompleted,
                     result: message.functionCallResult,
                  }]}
               />
               {renderThinkingIndicator(index)}
            </MessageBubble>
         );
      };

      const renderFileMessage = (
         message: ChatMessage,
         index: number,
         slot: (typeof turnMeta)[number],
         commonProps: ReturnType<typeof getCommonProps>
      ) => {
         const feedbackSource =
            slot.feedbackSourceIndex === undefined
               ? undefined
               : messages[slot.feedbackSourceIndex];
         const feedbackSlot =
            feedbackSource?.messageId &&
            hasFeedbackHandler.current &&
            (!isStreaming || !isLastAssistant(index)) ? (
               <MessageFeedback
                  messageId={feedbackSource.messageId}
                  currentRating={feedbackSource.feedback?.rating}
                  onFeedback={stableFeedbackHandler}
               />
            ) : undefined;

         const copyText =
            slot.copySourceIndex === undefined
               ? undefined
               : messages[slot.copySourceIndex].text;

         return (
            <MessageBubble
               type="file"
               {...commonProps}
               fileData={message.fileData!}
               fileName={message.text}
               autoOpen={!message.isUser}
               hideAgentHeader={slot.hideAgentHeader}
               feedbackSlot={feedbackSlot}
               copyText={copyText}
            />
         );
      };

      const renderScriptDraftMessage = (
         message: ChatMessage,
         index: number,
         slot: (typeof turnMeta)[number],
         commonProps: ReturnType<typeof getCommonProps>
      ) => {
         const feedbackSource =
            slot.feedbackSourceIndex === undefined
               ? undefined
               : messages[slot.feedbackSourceIndex];
         const feedbackSlot =
            feedbackSource?.messageId &&
            hasFeedbackHandler.current &&
            (!isStreaming || !isLastAssistant(index)) ? (
               <MessageFeedback
                  messageId={feedbackSource.messageId}
                  currentRating={feedbackSource.feedback?.rating}
                  onFeedback={stableFeedbackHandler}
               />
            ) : undefined;

         const copyText =
            slot.copySourceIndex === undefined
               ? undefined
               : messages[slot.copySourceIndex].text;

         return (
            <AskScriptDraftBubble
               isUser={commonProps.isUser}
               agentName={commonProps.agentName}
               brandOverride={commonProps.brandOverride}
               className={commonProps.className}
               scriptDraft={message.scriptDraftData!}
               isLastInGroup={commonProps.isLastInGroup}
               autoOpen={!message.isUser}
               hideAgentHeader={slot.hideAgentHeader}
               feedbackSlot={feedbackSlot}
               copyText={copyText}
            />
         );
      };

      const renderMessage = (message: ChatMessage, index: number) => {
         const nextMessage = messages[index + 1];
         const prevMessage = messages[index - 1];
         const commonProps = getCommonProps(message, index);
         const slot = turnMeta[index];

         if (message.fileData) {
            return renderFileMessage(message, index, slot, commonProps);
         }

         if (message.scriptDraftData) {
            return renderScriptDraftMessage(message, index, slot, commonProps);
         }

         if (message.buildWorkflowData) {
            // Nothing starts until the turn has finished streaming: the tool
            // card's button would otherwise appear mid-stream, and an after-call
            // build kicked off mid-stream would race the conversation persist
            // (which surfaced as a 403 on the GailGPT status endpoint). Once an
            // after-call build publishes, the bubble hands the continuation
            // message back through onSendMessage so the setup chat carries on.
            // The continuation only goes out while the chat is idle and this
            // card is still in the trailing assistant turn: once the operator
            // (or an earlier continuation) has spoken after it, the setup
            // conversation has moved on and must not be interrupted.
            const isMessageComplete = !isStreaming || !isLastAssistant(index);
            const canSendContinuation = !disabled && !isAiWorking && index > lastUserIndex;
            return (
               <AskWorkflowBuilderBubble
                  isUser={commonProps.isUser}
                  agentName={commonProps.agentName}
                  brandOverride={commonProps.brandOverride}
                  className={commonProps.className}
                  buildWorkflow={message.buildWorkflowData}
                  isLastInGroup={commonProps.isLastInGroup}
                  canApprove={!message.isUser && isMessageComplete}
                  canSendContinuation={canSendContinuation}
                  hideAgentHeader={slot.hideAgentHeader}
                  originAssistantId={selectedAssistant?.id}
                  onBuildPublished={(messageText) => onSendMessage(messageText)}
               />
            );
         }

         if (message.chartData) {
            if (canInlineChartWithNext(nextMessage)) return null;
            return <MessageBubble type="chart" {...commonProps} chartData={message.chartData} />;
         }

         if (message.functionCallData) {
            return renderFunctionCallMessage(message, index, commonProps);
         }

         // Inline the previous chart only when it deferred its own bubble, else it double-renders before a new user message.
         const prevMessageIsChart =
            !!prevMessage?.chartData &&
            !prevMessage.isUser &&
            canInlineChartWithNext(message);
         const hasContentParts = message.contentParts && message.contentParts.length > 0;
         const isMessageComplete = !isStreaming || !isLastAssistant(index);
         const suppressOwnControls = slot.suppressOwnControls;
         const showOwnControls =
            isMessageComplete && !message.isUser && !suppressOwnControls;

         const feedbackSlot =
            showOwnControls && message.messageId && hasFeedbackHandler.current ? (
               <MessageFeedback
                  messageId={message.messageId}
                  currentRating={message.feedback?.rating}
                  onFeedback={stableFeedbackHandler}
               />
            ) : undefined;
         const readAloudText = showOwnControls ? getMessageReadAloudText(message) : "";
         const readAloudSlot =
            readAloudText.trim() ? (
               <GailGptReadAloud
                  text={readAloudText}
                  conversationId={conversationId}
                  messageId={message.messageId}
                  messageIndex={index}
               />
            ) : undefined;
         const ticketConfirmChoices =
            index === messages.length - 1 &&
            !message.isUser &&
            isMessageComplete &&
            !isAiWorking
               ? message.contentParts
                  ? ticketConfirmationChoicesFromParts(message.contentParts)
                  : parseTicketConfirmationChoices(message.functionCallResult)
               : null;

         return (
            <MessageBubble
               type="regular"
               {...commonProps}
               className={clsx(
                  commonProps.className,
                  showOwnControls && "pb-3",
                  suppressOwnControls && "[&]:mb-2"
               )}
               copyText={showOwnControls ? message.text : undefined}
               readAloudSlot={readAloudSlot}
               feedbackSlot={feedbackSlot}
            >
               {hasContentParts ? (
                  <ApprovalContentParts
                     conversationId={conversationId}
                     contentParts={message.contentParts!}
                     messageId={message.messageId}
                     approvalMessageIds={message.approvalMessageIds}
                     activeMessageId={activeToolApprovalMessageId}
                     disabled={disabled || isAiWorking || isStreaming}
                     onRespond={onToolApprovalResponse}
                     messageIndex={index}
                     isUser={message.isUser}
                     isStreaming={!isMessageComplete}
                     citations={message.citations}
                  />
               ) : (
                  <AskText
                     text={message.text}
                     isMarkDown={!message.isUser}
                     variant={message.isUser ? "default" : "v1"}
                     citations={message.citations}
                  />
               )}
               {ticketConfirmChoices && (
                  <TicketConfirmSuggestions
                     choices={ticketConfirmChoices}
                     onSelect={(userText) => onSendMessage(userText)}
                  />
               )}
               {!message.isUser && message.citations && message.citations.length > 0 && (
                  <SourceFooter citations={message.citations} />
               )}
               {!message.isUser && message.recalledFacts && message.recalledFacts.length > 0 && (
                  <RecalledMemoriesFooter
                     facts={message.recalledFacts}
                     model={selectedAssistant?.model}
                  />
               )}
               {renderThinkingIndicator(index)}
               {prevMessageIsChart && (
                  <div className="mt-4">
                     <div className="bg-background rounded-2xl border border-neutral py-4">
                        {renderChartContent(prevMessage, stableT, theme, brand)}
                     </div>
                  </div>
               )}
            </MessageBubble>
         );
      };

      // First slot of the trailing run of user messages (the text plus any
      // attachment cards of one send) — the pin target for a fresh turn.
      let lastUserTurnStart = lastUserIndex;
      while (lastUserTurnStart > 0 && messages[lastUserTurnStart - 1].isUser) {
         lastUserTurnStart -= 1;
      }

      const renderSlot = (message: ChatMessage, index: number) => {
         const renderedMessage = renderMessage(message, index);
         if (!renderedMessage) return null;

         return (
            <div
               key={"message-" + index}
               data-last-user-turn={index === lastUserTurnStart ? "true" : undefined}
               className={clsx(
                  "px-3 md:px-6",
                  message.isUser ? "flex justify-end" : ""
               )}
            >
               {renderedMessage}
            </div>
         );
      };

      // Anchor the whole trailing assistant turn (text + cards + charts) in a
      // single viewport-sized container so the response pins toward the top of
      // the scroll area. Applying the min-height per-message would re-reserve
      // the full space below trailing bubbles (e.g. a script draft card) and
      // leave a large empty gap after content that already fills the viewport.
      const tailStart = lastUserIndex + 1;
      const anchorTailTurn =
         !showInitialThinking &&
         tailStart < messages.length &&
         lastAssistantMessageIndex >= tailStart;

      return (
         <>
            {anchorTailTurn ? (
               <>
                  {messages.slice(0, tailStart).map((m, i) => renderSlot(m, i))}
                  <div key="tail-turn" className={anchorTurnClass}>
                     {messages.slice(tailStart).map((m, i) => renderSlot(m, tailStart + i))}
                  </div>
               </>
            ) : (
               messages.map((m, i) => renderSlot(m, i))
            )}
            {showInitialThinking && (
               <div className={clsx(
                  "px-3 md:px-6 transition-opacity duration-150",
                  anchorTurnClass,
                  "mb-8"
               )}>
                  <MessageBubble
                     type="regular"
                     isUser={false}
                     agentName={senderName}
                     themeOverride={theme}
                     brandOverride={brand}
                     className=""
                  >
                     {renderThinkingContent()}
                  </MessageBubble>
               </div>
            )}
         </>
      );
   // Deps are intentionally complete: AssistantContentParts, renderChartContent, and
   // canInlineChartWithNext are module-level functions and don't need to be listed.
   //
   // Note: `messages` changes on every streaming token, so this memo
   // re-computes during streaming. The main benefit is preventing re-renders when
   // the user types in the input. Per-message React.memo would be more granular
   // but requires a larger refactor.
   }, [messages, conversationId, isAiWorking, isStreaming, streamingStatus, senderName, selectedAssistant?.model, theme, brand, stableT, stableFeedbackHandler, anchorTurnClass, lastUserIndex, onSendMessage, onToolApprovalResponse, activeToolApprovalMessageId, disabled]);

   return (
      <FileDropZone
         onFilesSelected={onFilesSelected ?? (() => {})}
         disabled={disabled || isSubmitting || !fileUploadConfig?.enabled}
         acceptedFileTypes={fileUploadConfig?.acceptedFileTypes ?? ""}
         allowMultiple={(fileUploadConfig?.maxFileCount ?? 1) > 1}
         fileUploadConfig={fileUploadConfig}
         assistantModel={selectedAssistant?.model}
         onInvalidFiles={onInvalidFiles}
         showAlerts={true}
         // In fill mode the drop-zone wrapper must pass the parent's height
         // through, or the chat root's h-full resolves against an auto-height
         // div — the conversation then grows unbounded instead of scrolling.
         className={fillContainer ? "h-full" : undefined}
      >
         <div
            className={clsx(
               "flex flex-col pb-6",
               // Embedded surfaces (assistant dock) size the chat themselves;
               // standalone chat pages own the viewport height.
               fillContainer && "h-full",
               !fillContainer &&
                  (isBannerActive
                     ? "md:h-[calc(100vh-70px)] h-[calc(100vh-140px)]"
                     : "h-[calc(100vh-70px)] md:h-[100vh]"),
               "mx-auto min-h-0 items-center",
               className
            )}
         >
            <div
               ref={chatContainerRef}
               className={clsx(
                  "min-h-0 flex-1 overflow-y-auto w-full relative pt-8 pb-4",
                  // Query container so an expanded table can break out to this
                  // width (cqw) instead of the 768px column. `size` (both axes)
                  // also exposes cqw; `inline-size` is enough otherwise.
                  fillContainer ? "[container-type:size]" : "[container-type:inline-size]"
               )}
            >
               <DocumentPreviewProvider value={openDocument}>
                  <div className="max-w-[768px] mx-auto text-foreground">
                     {renderedMessageList}
                  </div>
               </DocumentPreviewProvider>
            </div>

            <DocumentPreviewSheet {...previewProps} />

            {/* Fixed input at bottom */}
            <ChatInput
               skills={displaySkills}
               isLoadingSkills={skills === null}
               value={inputValue}
               onChange={onInputChange}
               onSubmit={onSendMessage}
               disabled={disabled || isLoadingAssistants}
               isSubmitting={isSubmitting || isStreaming}
               onCancelStream={onCancelStream}
               placeholder={inputPlaceholder}
               variant="compact"
               className={clsx(
                  "shrink-0 -mt-4 transition-opacity duration-300 p-2 pt-0 md:px-6 opacity-100 w-full text-foreground",
                  "max-w-[768px]"
               )}
               assistants={assistants}
               selectedAssistant={selectedAssistant}
               onAssistantChange={onAssistantChange}
               showAssistantSelector={false}
               canEditModel={canEditModel}
               conversationId={conversationId}
               onModelChange={onModelChange}
               isModelSaving={isModelSaving}
               onFilesSelected={onFilesSelected}
               fileUploadConfig={fileUploadConfig}
               attachedFiles={attachedFiles}
               onFilesChange={onFilesChange}
               fileCompatibilityTooltip={fileCompatibilityTooltip}
               tokenUsagePercent={tokenUsagePercent}
               tokenLimitReached={tokenLimitReached}
               tokenBreakdown={tokenBreakdown}
               enableDictation
               enableMemory
            />
            <div className="shrink-0 text-center text-600 px-2 text-xs -mt-1">
               <p>{t("chatDisclaimer")}</p>
            </div>
         </div>
      </FileDropZone>
   );
};

export default ChatView;
